/**
 * PILL TRAVERSAL FOR THE UNIFIED RUNG LADDER — the same RULES as `tfKeyTarget`, on the wider axis.
 *
 * ⛔ WHY THIS IS NOT A CHANGE TO `tfSwitch.ts`, AND THE REASON IS A KEY, NOT A PREFERENCE.
 * `TfSwitcher.svelte` has ONE production consumer (`EsChartPanel:1139`) and `tfSwitch.test.ts`
 * carries SOURCE-STRUCTURAL arms that read that file's bytes. Widening its types to `Rung` would
 * move blobs the gates have already graded — *"a key does not carry across a blob move"* — and
 * would put the cockpit's own switcher on the critical path of a new page. The cockpit's control
 * keeps working, untouched, on the axis it was driven for.
 *
 * ⚠ BUT SHARING THE *RULES* IS NOT OPTIONAL, AND THIS IS THE HALF THAT ROTS QUIETLY: two controls
 * that traverse differently teach the owner two different keyboards on one page. So the rules are
 * not re-decided here — they are re-stated and then **pinned against `tfKeyTarget` by an arm that
 * drives both**. ⇒ ***A SECOND IMPLEMENTATION IS A FORK THE MOMENT NOTHING COMPARES IT TO THE
 * FIRST.***
 *
 * THE RULES, inherited verbatim from `tfKeyTarget` and its reasons:
 *   · arrows move by one, Home/End jump to the ends
 *   · ⛔ NO WRAP. One keypress past the last rung must not land on the first: on this ladder that
 *     is a `1d -> 5s` jump of 17,280x, on a surface where the rung decides what every number on
 *     screen means. `tfSwitch.ts` refused wrapping at 1,440x; the wider ladder makes it worse.
 *   · an unhandled key returns null and stays unhandled — the control never swallows a keystroke
 *     it does not act on.
 */

import { rungKey, type Rung } from "./rungLadder.ts";

/**
 * The rung an arrow/Home/End should move to, or `null` when the key is not ours or the move would
 * leave the ladder.
 *
 * ⚠ `current` NOT BEING IN `options` YIELDS null, NEVER options[0]. A control whose selection is
 * off its own list is in a state nobody designed; snapping silently to the first rung would hide
 * that and move the owner's chart underneath him. `tfKeyTarget` refuses it the same way.
 */
export function rungKeyTarget(
  options: readonly Rung[],
  current: Rung,
  key: string,
): Rung | null {
  if (options.length === 0) return null;
  const i = options.findIndex((o) => rungKey(o) === rungKey(current));
  if (i === -1) return null;
  switch (key) {
    case "ArrowLeft":
    case "ArrowUp":
      return i > 0 ? options[i - 1] : null;
    case "ArrowRight":
    case "ArrowDown":
      return i < options.length - 1 ? options[i + 1] : null;
    case "Home":
      return options[0];
    case "End":
      return options[options.length - 1];
    default:
      return null;
  }
}

/**
 * ⛔⛔ GK-ac's COND-1, PINNED AS A FACT RATHER THAN CARRIED AS A CAUTION.
 *
 * ac keyed `5979e7d490` with one hole named: `Number(rung) * 60_000` **still compiles**, because
 * `Number()` accepts `any` and no discriminated union can close that. Driven to runtime by ac:
 * ```
 *   key    Number(rung)   label via that path   rungSpanMs
 *   s:5s      NaN            'NaNm'                5000
 *   t:1       NaN            'NaNm'               60000     <- EVERY rung, not just sub-minute
 * ```
 * ⭐ **AND ac's REASON FOR KEYING RATHER THAN BLOCKING IS THE PROPERTY THIS FUNCTION PRESERVES:**
 * the defect the union replaced was insidious *because it was PARTIAL* — minute rungs kept working
 * while only `5s` went NaN, so it shipped green. **This residual breaks all seven rungs and paints
 * `NaNm` on every pill.** ⇒ ***IT CANNOT SHIP QUIETLY THE WAY THE DEFECT IT REPLACES COULD.***
 *
 * So the cure is not to make `Number(rung)` safe — it is to make the total failure *checkable*.
 * A consumer computes width from THIS, and the arm below pins that every rung is finite through it
 * and NaN through the coercion, so the day someone reaches for `Number` the suite says which.
 */
export function pillSpanMs(r: Rung, spanOf: (r: Rung) => number): number {
  const ms = spanOf(r);
  if (!Number.isFinite(ms) || ms <= 0) {
    // Fail LOUD (law 1): a non-finite span silently mis-grades every bucket downstream.
    throw new RangeError(
      `rung ${rungKey(r)} produced a non-finite span (${ms}) — did a caller reach for Number(rung)?`,
    );
  }
  return ms;
}

/**
 * ⛔⛔ WHICH PILL IS THE TAB STOP — the roving half of the WAI-ARIA radiogroup.
 *
 * `codex review` found, and GK-ad drove (`bh=49cc51c918`), that the component computed this inline
 * as `sameRung(o, current) ? 0 : -1`. When `current` is NOT among `options` that yields
 * `[-1, -1, -1]` — and `rungKeyTarget` returns null for every key in that same state — so
 * ***THE RADIOGROUP RENDERS, IS VISIBLE, AND IS WHOLLY INOPERABLE BY KEYBOARD.***
 *
 * ⚠ THE RULE LIVES HERE, NOT IN THE MARKUP, AND THAT IS THE REAL CURE. ad's other point was that
 * nine green arms could not SEE this class, because the rule was an expression inside a `.svelte`
 * file that only a text-grep could reach — and an arm pinned to a spelling breaks on a correct
 * edit (it did: my first fix turned arm 3 red without changing any behaviour). Moved here it is a
 * pure function over real `Rung`s, so it is armed BEHAVIOURALLY like every other rule in this file.
 *
 * ⛔ IT DOES NOT SNAP THE SELECTION. `rungKeyTarget` deliberately refuses to treat an off-list
 * `current` as `options[0]` — that refusal is armed and pinned against `tfKeyTarget`, because
 * snapping would move the owner's chart underneath him. This returns a FOCUS TARGET, not a
 * selection: WAI-ARIA's rule that a radiogroup with nothing checked puts its tab stop on the FIRST
 * radio. The anomaly stays visible — no pill is `aria-checked` — and the reader can still get in.
 */
export function tabStopIndex(options: readonly Rung[], current: Rung): number {
  if (options.length === 0) return -1; // nothing painted, nothing to focus
  const i = options.findIndex((o) => rungKey(o) === rungKey(current));
  return i === -1 ? 0 : i;
}
